import React, { Fragment, Component } from 'react';
import { withAsyncActions } from 'react-async-client';
import { Field, FormSpy } from 'react-final-form';
import Translate from 'react-translate-component';
import { find, isNil, path, propEq } from 'ramda';
import { Radio, Form } from 'antd';

import { formItemLayout } from 'constants/formItemLayout';
import { LEGAL_REQUEST_TYPES, LEGAL_REQUEST_NEW } from 'constants/request';
import { getLegalOrganizations, getLegalDepartment, getLegalDepartmentCells } from 'actions/asyncActions';
import Select from 'components/common/form/Select';
import RadioButtons from 'components/common/form/RadioButtons';
import { required } from 'utils/validators';

@withAsyncActions(props => ({
    getLegalDepartment: getLegalDepartment
        .withPayload(props => ({
            company: props.legal
        }))
        .withOptions({
            dispatchOnUpdate: !isNil(props.legal),
            autoReset: true,
            dispatchOnMount: !isNil(props.legal)
        })
        .withSuccessHandler(({ getLegalDepartment: { data }, form: { change }, department }) => {
            if (data.length) {
                const item = data.length === 1 ? data[0] : find(propEq('id', Number(department)))(data || []);

                if (item) {
                    change('kadrsyst.department', item.id);
                    change('kadrsyst.departchiefkey', path(['chief', 'id'], item) || null);
                    change('kadrsyst.chief', path(['chief', 'name'], item) || '');
                    change('kadrsyst.rolecode', path(['role', 'id'], item) || null);
                } else {
                    change('kadrsyst.department', null);
                    change('kadrsyst.departchiefkey', null);
                    change('kadrsyst.chief', null);
                    change('kadrsyst.rolecode', null);
                }
            }
        })
        .withErrorHandler(({ form: { change }, department }) => {
            if (department) {
                change('kadrsyst.legal', null);
                change('kadrsyst.department', null);
                change('kadrsyst.departchiefkey', null);
                change('kadrsyst.chief', null);
                change('kadrsyst.rolecode', null);
                change('kadrsyst.cell', null);
                change('kadrsyst.employee', null);
                change('routeType', null);
            }
        })
}))
class LegalField extends Component {
    onLegalChange = (value, option) => {
        if (value) {
            this.props.form.change('kadrsyst.departchiefkey', path(['data', 'chief', 'id'], option) || null);
            this.props.form.change('kadrsyst.chief', path(['data', 'chief', 'name'], option) || '');
            this.props.form.change('kadrsyst.rolecode', path(['data', 'role', 'id'], option) || null);
            this.props.form.change('kadrsyst.cell', null);
            this.props.form.change('kadrsyst.employee', null);
            this.props.form.change('routeType', null);
        } else {
            this.props.form.change('kadrsyst.departchiefkey', null);
            this.props.form.change('kadrsyst.chief', null);
            this.props.form.change('kadrsyst.rolecode', null);
            this.props.form.change('kadrsyst.cell', null);
            this.props.form.change('kadrsyst.employee', null);
            this.props.form.change('routeType', null);
        }
    }

    render() {
        const { getLegalDepartment, legal, disabled } = this.props;

        return <Field
            name={'kadrsyst.department'}
            allowClear
            showSearch
            component={Select}
            options={getLegalDepartment.data}
            disabled={!legal || disabled}
            dictionaryAction={getLegalDepartment}
            label={{ content: 'request.legalDepartment', required: true }}
            className='form-control'
            placeholder={<Translate content='placeholders.chooseLegalDepartment' />}
            onChange={this.onLegalChange}
            validate={required}
        />;
    }
}

const LEGAL_CELL_MODES = {
    cells: 'cells',
    employees: 'employees',
};

@withAsyncActions(props => ({
    getLegalDepartmentCells: getLegalDepartmentCells
        .withPayload(props => ({
            department: props.department,
        }))
        .withOptions({
            dispatchOnUpdate:! isNil(props.department),
            autoReset: true,
            dispatchOnMount: !isNil(props.department)
        })
}))
class LegalCellField extends Component {
    state = {
        mode: LEGAL_CELL_MODES.cells
    }

    onLegalCellChange = (value, option) => {
        if (value) {
            if (this.state.mode === LEGAL_CELL_MODES.cells) {
                this.props.form.change('position', path(['data', 'name'], option) || null);
            } else {
                this.props.form.change('kadrsyst.cell', path(['data', 'cell', 'id'], option) || null);
                this.props.form.change('position', path(['data', 'cell', 'name'], option) || null);
            }
        } else {
            this.props.clearBase();
        }
    }

    render() {
        const { getLegalDepartmentCells: { data: cells }, getLegalDepartmentCells, disabled, cell } = this.props;
        const item = (cells || []).find(item  => item.id === Number(cell));
        const employees = (cells || []).reduce((acc, cell) => {
            return [...acc, ...(cell.employees || []).map(employee => ({
                ...employee,
                cell,
                name: `${employee.text} (${cell.name})`,
            }))];
        }, []);

        return (
            <Fragment>
                <Form.Item wrapperCol={formItemLayout.textCol}>
                    <Radio.Group value={this.state.mode} onChange={e => this.setState({ mode: e.target.value })}>
                        <Radio.Button value={LEGAL_CELL_MODES.cells}>
                            <Translate content='request.cellsMode' />
                        </Radio.Button>
                        <Radio.Button  value={LEGAL_CELL_MODES.employees}>
                            <Translate content='request.employeesMode' />
                        </Radio.Button>
                    </Radio.Group>
                </Form.Item>
                <Field
                    name='kadrsyst.employee'
                    component={Select}
                    options={employees}
                    containerClass={this.state.mode === LEGAL_CELL_MODES.cells ? 'hide' : ''}
                    disabled={disabled}
                    dictionaryAction={getLegalDepartmentCells}
                    label={{ content: 'request.legalCell', required: true }}
                    placeholder={<Translate content='placeholders.chooseLegalCell' />}
                    onChange={this.onLegalCellChange}
                />
                <Field
                    name='kadrsyst.cell'
                    component={Select}
                    options={cells}
                    disabled={disabled}
                    dictionaryAction={getLegalDepartmentCells}
                    label={{ content: 'request.legalCell', required: true }}
                    fieldClass={this.state.mode === LEGAL_CELL_MODES.cells ? '' : 'sub-error-top ant-col-sm-offset-8 ant-col-md-offset-8'}
                    fieldClassName={this.state.mode === LEGAL_CELL_MODES.cells ? '' : 'hide'}
                    notWrap={this.state.mode !== LEGAL_CELL_MODES.cells}
                    placeholder={<Translate content='placeholders.chooseLegalCell' />}
                    onChange={this.onLegalCellChange}
                    validate={required}
                />
                { cell ? (
                    <Form.Item
                        labelCol={formItemLayout.labelCol}
                        wrapperCol={formItemLayout.wrapperCol}
                        label={<Translate content='request.legalCellOptions' />}
                    >
                        <div><Translate content='request.legalCellGrade' />: <b>{`${item ? path(['grade'], item) : ''}`}</b></div>
                        <div><Translate content='request.legalCellNumrates' />: <b>{`${item ? path(['numrates'], item) : ''}`}</b></div>
                    </Form.Item>
                ) : null}
            </Fragment>
        );
    }
}

@withAsyncActions({
    getLegalOrganizations: getLegalOrganizations
        .withOptions({ dispatchOnMount: true, autoReset: true })
        .withSuccessHandler(({ getLegalOrganizations: { data }, form: { change }, legal }) => {
            if ((data.length === 1) && !legal) {
                change('kadrsyst.legal', data[0].id);
            }
        })
})
export default class LegalFields extends Component {
    onLegalChange = () => {
        this.props.form.change('kadrsyst.departchiefkey', null);
        this.props.form.change('kadrsyst.chief', null);
        this.props.form.change('kadrsyst.rolecode', null);
        this.props.form.change('kadrsyst.cell', null);
        this.props.form.change('kadrsyst.employee', null);
        this.props.form.change('routeType', null);
    }

    clearBase = () => {
        this.props.form.change('position', '');
    }

    render() {
        const {
            form,
            getLegalOrganizations: { data: organizations }, getLegalOrganizations,
        } = this.props;

        return <Fragment>
            <Field
                name='kadrsyst.legal'
                component={Select}
                options={organizations}
                dictionaryAction={getLegalOrganizations}
                label={{ content: 'request.legal', required: true }}
                placeholder={<Translate content='placeholders.chooseLegal' />}
                onChange={this.onLegalChange}
                validate={required}
            />

            <FormSpy subscription={{ values: true }}>
                { ({ values }) => (
                    <Fragment>
                        <LegalField
                            form={form}
                            legal={path(['kadrsyst', 'legal'], values)}
                            department={path(['kadrsyst', 'department'], values)}
                        />
                        {path(['kadrsyst', 'department'], values) ? (
                            <Field
                                name='routeType'
                                component={RadioButtons}
                                options={LEGAL_REQUEST_TYPES}
                                label={{ content: 'request.legalRoute' }}
                                onChange={this.props.clearBase}
                                radioTranslate='request.legalRoutes'
                                validate={required}
                            />
                        ) : null}
                        {path(['routeType'], values) && (path(['routeType'], values) !== LEGAL_REQUEST_NEW) ? (
                            <LegalCellField
                                form={form}
                                cell={path(['kadrsyst', 'cell'], values)}
                                legal={path(['kadrsyst', 'legal'], values)}
                                department={path(['kadrsyst', 'department'], values)}
                                clearBase={this.clearBase}
                            />
                        ) : null}
                    </Fragment>
                )}
            </FormSpy>
        </Fragment>;
    }
}
